import type { ILegacyIndustryFilterDto } from "app/pages/sales-intelligence/domain/industry/types/filter-values";
import type { ISiteTagsDto } from "app/pages/sales-intelligence/domain/leads/types/filter-values";
import type { FiltersRoutingParams } from "app/pages/sales-intelligence/modules/leads-search/types/routing";
import { SiteTagsFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters/models/SiteTagsFilter/SiteTagsFilterValue";
import type { InitialLeadsSeed } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/initialSeed";
import { urlFilterValues } from "app/pages/sales-intelligence/modules/leads-search-filters/utils/urlFilterValues";
import type { SiAdvancedSearchFiltersParams } from "app/pages/sales-intelligence/services/navigation/types";

type SeedParams = Partial<SiAdvancedSearchFiltersParams> & Pick<FiltersRoutingParams, "filtersPayload">;

const fromURLParam = <K extends keyof SiAdvancedSearchFiltersParams, T>(
    urlParams: Partial<SiAdvancedSearchFiltersParams>,
    key: K,
    transform: (value: NonNullable<SiAdvancedSearchFiltersParams[K]>) => T,
): T | undefined => {
    const value = urlParams[key];

    if (!value) {
        return undefined;
    }

    return transform(value as NonNullable<SiAdvancedSearchFiltersParams[K]>);
};

/** @deprecated Read-only support for links minted before `filtersPayload`. Never mint this param. */
const getIndustryValueFromURLParams = (urlParams: Partial<SiAdvancedSearchFiltersParams>) =>
    fromURLParam(
        urlParams,
        "industry",
        (industry): ILegacyIndustryFilterDto => urlFilterValues.legacyIndustry(industry).industryFilter,
    );

/** @deprecated Read-only support for links minted before `filtersPayload`. Never mint this param. */
const getSiteTagsValueFromURLParams = (
    urlParams: Partial<SiAdvancedSearchFiltersParams>,
): readonly ISiteTagsDto[] | undefined =>
    fromURLParam(urlParams, "siteTags", (value) => [SiteTagsFilterValue.toDtoValue({ values: [value] })]);

/**
 * The payload slot, keyed by filter key. Only its shape is guarded here — a malformed payload already
 * decoded to `null` (see `lenientJsonParamType`), and keys the panel→DTO fold cannot route are dropped by
 * the fold itself, so nothing unroutable reaches redux or the wire however it got in.
 */
const getPayloadFromURLParams = (urlParams: SeedParams): InitialLeadsSeed => {
    const { filtersPayload } = urlParams;

    if (!filtersPayload || typeof filtersPayload !== "object" || Array.isArray(filtersPayload)) {
        return {};
    }

    return filtersPayload;
};

/**
 * Translates advanced-search deep-link URL params into seed filter values, keeping URL/routing knowledge at
 * the page that owns the URL (the leads-search-filters module trades only in filter values).
 *
 * The payload is applied last so it wins on any key a deprecated alias also set — a link carrying both is
 * one being migrated, and the new shape is the one the sender meant.
 */
export const toInitialFilterValues = (urlParams: SeedParams): InitialLeadsSeed => {
    const industryFilter = getIndustryValueFromURLParams(urlParams);
    const siteTags = getSiteTagsValueFromURLParams(urlParams);

    return {
        ...(industryFilter ? { industryFilter } : {}),
        ...(siteTags ? { siteTags } : {}),
        ...getPayloadFromURLParams(urlParams),
    };
};
